import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchRecipes, unsaveRecipe } from '../../store/recipesSlice';
import { Link, NavLink } from 'react-router-dom';
import useCsrfToken from '../../hooks/useCsrfToken';

interface Props {
  scope: 'all' | 'mine' | 'saved';
}

const VisibilityBadge: React.FC<{ visibility: 'public' | 'private' }> = ({ visibility }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
    visibility === 'public'
      ? 'bg-army-800 text-neon border border-army-700'
      : 'bg-navy-800 text-gray-400 border border-navy-700'
  }`}>
    {visibility === 'public' ? 'Public' : 'Private'}
  </span>
);

const RecipeIndex: React.FC<Props> = ({ scope }) => {
  const dispatch = useAppDispatch();
  const csrfToken = useCsrfToken();
  const { items: recipes, status, error } = useAppSelector((state) => state.recipes);
  const user = useAppSelector((state) => state.auth.user);

  useEffect(() => {
    dispatch(fetchRecipes(scope));
  }, [scope, dispatch]);

  const tabClass = (active: boolean) =>
    `px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
      active
        ? 'bg-navy-800 text-white border border-navy-700'
        : 'text-gray-500 hover:text-white'
    }`;

  const isEmpty = recipes.length === 0 && status === 'succeeded';

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <NavLink to="/recipes" end className={({ isActive }) => tabClass(isActive)}>
            All Recipes
          </NavLink>
          {user && (
            <>
              <NavLink to="/recipes/mine" className={({ isActive }) => tabClass(isActive)}>
                My Recipes
              </NavLink>
              <NavLink to="/recipes/saved" className={({ isActive }) => tabClass(isActive)}>
                Saved
              </NavLink>
            </>
          )}
        </div>
        {scope === 'mine' && user && (
          <Link
            to="/recipes/new"
            className="px-4 py-2 bg-neon hover:brightness-90 text-army-950 font-bold text-sm rounded-lg transition-all"
          >
            + New Recipe
          </Link>
        )}
      </div>

      {status === 'loading' && (
        <div className="text-army-600 py-12 text-center">Loading recipes...</div>
      )}
      {status === 'failed' && (
        <div className="text-rose-400 py-12 text-center">Error: {error}</div>
      )}

      {isEmpty && (
        <div className="text-center py-16 text-army-600">
          {scope === 'mine' && (
            <>No recipes yet. <Link to="/recipes/new" className="text-neon hover:brightness-90">Add your first one.</Link></>
          )}
          {scope === 'saved' && 'No saved recipes yet. Browse All Recipes to save some.'}
          {scope === 'all' && 'No public recipes yet.'}
        </div>
      )}

      {recipes.length > 0 && (
        <div className="rounded-xl border border-navy-800 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-navy-900 border-b border-navy-800">
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Recipe</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Ingredients</th>
                {scope === 'mine' && (
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Visibility</th>
                )}
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-army-900">
              {recipes.map((recipe) => (
                <tr key={recipe.id} className="bg-army-950 hover:bg-army-900 transition-colors group">
                  <td className="px-5 py-4 font-medium">
                    <Link to={`/recipes/${recipe.id}`} className="text-white hover:text-neon transition-colors">
                      {recipe.name}
                    </Link>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex flex-wrap gap-1">
                      {recipe.ingredients?.map((ingredient) => (
                        <span
                          key={ingredient.id}
                          className="inline-block px-2 py-0.5 bg-army-900 text-gray-500 text-xs rounded border border-army-800 group-hover:border-army-700 transition-colors"
                        >
                          {ingredient.food_name}
                        </span>
                      ))}
                    </div>
                  </td>
                  {scope === 'mine' && (
                    <td className="px-5 py-4">
                      <VisibilityBadge visibility={recipe.visibility} />
                    </td>
                  )}
                  <td className="px-5 py-4 text-right">
                    {scope === 'mine' && (
                      <Link
                        to={`/recipes/${recipe.id}/edit`}
                        className="text-xs font-medium px-3 py-1.5 rounded-md bg-navy-800 hover:bg-navy-700 text-gray-300 hover:text-white border border-navy-700 transition-colors"
                      >
                        Edit
                      </Link>
                    )}
                    {scope === 'saved' && (
                      <button
                        onClick={() => dispatch(unsaveRecipe(recipe.id))}
                        className="text-xs font-medium px-3 py-1.5 rounded-md bg-navy-800 hover:bg-navy-700 text-gray-300 hover:text-white border border-navy-700 transition-colors"
                      >
                        Unsave
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default RecipeIndex;
